import { FC, useEffect, useState } from 'react';
import { Form, Input, Upload, Spin } from 'antd';
import { IBankAccountDetails } from '../constants';
import ImgCrop from 'antd-img-crop';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useGetProfileSignedUrlMutation } from '../services/authApi';
import { useUploadFileMutation } from 'services/api';
import { AuthButtonComponent } from '../components/auth-button/auth-button.component';
import { beforeImage5mbUpload } from 'utils';

interface IDesignLabAccountDetailsComponentProps {
  initialValues?: IBankAccountDetails;
  submittedValues: Function;
  handleNextButton: Function;
  handleBackButton: Function;
  formSubmitting: boolean;
}

const AccountDetailsForm: FC<IDesignLabAccountDetailsComponentProps> = ({
  initialValues,
  submittedValues,
  handleNextButton,
  handleBackButton,
  formSubmitting,
}) => {
  const [loading, setLoading] = useState(false);
  const [profilePic, setProfilePic] = useState(initialValues?.profilePic || '');
  const [isProfilePicUploading, setIsProfilePicUploading] = useState(false);
  const [getProfileSignedUrl] = useGetProfileSignedUrlMutation();
  const [uploadFile] = useUploadFileMutation();
  const [form] = Form.useForm();

  useEffect(() => {
    if (initialValues?.profilePic) {
      setProfilePic(initialValues?.profilePic);
    }
  }, []);

  const onFinish = (values: any) => {
    submittedValues('bankDetails', { ...values, profilePic });
    handleNextButton({ ...values, profilePic });
  };

  const onFinishFailed = ({ errorFields }: any) => {
    form.scrollToField(errorFields[0].name, { block: 'center' });
  };

  const storeValues = async () => {
    const values = await form.getFieldsValue();
    submittedValues('bankDetails', { ...values, profilePic });
    handleBackButton();
  };

  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }

    return e && e.fileList;
  };

  const profileOnChange = (info: any) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      return;
    }
    if (info.file.status === 'done') {
      setLoading(false);
    }
  };

  const profileRequest = async ({ file, onSuccess }: any) => {
    onSuccess('ok');
    if (file) {
      if (profilePic) {
        setProfilePic('');
      }
      setIsProfilePicUploading(true);
      try {
        const fileData = {
          fileName: file.name,
          mimeType: file.type,
          folderName: 'profile',
        };
        const signedUrl = await getProfileSignedUrl(fileData).unwrap();
        await uploadFile({ url: signedUrl.url, file });
        const imageUrl = signedUrl && signedUrl.url.split('?')[0];
        if (imageUrl) {
          setIsProfilePicUploading(false);
        }
        setProfilePic(imageUrl);
      } catch (e) {
        setIsProfilePicUploading(false);
        // console.log('Upload profile pic error', e);
      }
    }
  };

  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      initialValues={initialValues}
      layout="vertical"
      size="large"
      autoComplete="off"
      className="mt-3 fadein animation-duration-500"
    >
      <div className="grid">
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="bankName" label="Bank Name">
            <Input className="custom-input" placeholder="Enter bank name" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="bankCountry" label="Bank Country">
            <Input className="custom-input" placeholder="Enter bank country" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="accountHolderName" label="Name of account holder">
            <Input className="custom-input" placeholder="Account holder" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="accountNumber" label="Account number">
            <Input className="custom-input" placeholder="Account number" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="sortCode" label="Sort code (if UK bank)">
            <Input className="custom-input" placeholder="Enter sort code" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item name="iban" label="IBAN (if international)">
            <Input className="custom-input" placeholder="Enter IBAN" />
          </Form.Item>
        </div>
        <div className="col-12 sm:col-12 md:col-6">
          <Form.Item label="Would you like to upload a profile picture?" className="auth-upload-avatar">
            <Form.Item name="profilePic" valuePropName="fileList" getValueFromEvent={normFile} noStyle>
              <ImgCrop rotate>
                <Upload
                  listType="picture-card"
                  showUploadList={false}
                  beforeUpload={beforeImage5mbUpload}
                  customRequest={profileRequest}
                  onChange={profileOnChange}
                >
                  {profilePic ? (
                    <img src={profilePic} alt="avatar" style={{ width: '100%', borderRadius: '10px' }} />
                  ) : isProfilePicUploading ? (
                    <Spin size="small" />
                  ) : (
                    uploadButton
                  )}
                </Upload>
              </ImgCrop>
            </Form.Item>
          </Form.Item>
        </div>
      </div>

      <div className="steps-action">
        <Form.Item>
          <AuthButtonComponent outline onClick={storeValues} className="mr-2 md:mr-4">
            Previous
          </AuthButtonComponent>
          <AuthButtonComponent isLoading={formSubmitting}>Submit</AuthButtonComponent>
        </Form.Item>
      </div>
    </Form>
  );
};

export { AccountDetailsForm };
